// BoostInfoModal.js
import React from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Trans, useTranslation } from "react-i18next";

const BoostInfoModal = ({ open, onClose }) => {
  const { t } = useTranslation();

  /* ---------- styles ---------- */

  const bodyTextSx = {
    color: "rgba(0,0,0,0.65)",
    fontFamily: "Nunito, sans-serif",
    fontSize: "13px",
    lineHeight: 1.5,
  };

  const blueTitleSx = {
    color: "rgb(29, 106, 222)",
    fontFamily: "Nunito, sans-serif",
    fontSize: "14px",
    fontWeight: 700,
    mb: 1,
  };

  const sectionLabelSx = {
    fontFamily: "Nunito, sans-serif",
    fontSize: "13px",
    fontWeight: 800,
    color: "rgb(102,102,102)",
    mb: 0.6,
  };

  const strongComponent = (
    <Box
      component="strong"
      sx={{
        fontWeight: 700,
        color: "rgb(102,102,102)",
        fontFamily: "Nunito, sans-serif",
      }}
    />
  );

  const bulletRow = (i18nKey) => (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box sx={{ mt: "2px" }}>•</Box>
      <Typography sx={bodyTextSx}>
        <Trans i18nKey={i18nKey} components={{ strong: strongComponent }} />
      </Typography>
    </Stack>
  );

  /* ---------- render ---------- */

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          width: "100%",
          maxWidth: "450px",
          maxHeight: { xs: "90vh", sm: "auto" },
          overflowY: "auto",
          p: { xs: 2, sm: 4 },
          position: "relative",
        },
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 20,
          color: "rgba(0,0,0,0.45)",
          zIndex: 2,
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Header */}
      <Box
        component="h2"
        sx={{
          display: "flex",
          alignItems: "center",
          columnGap: "10px",
          color: "rgb(255, 137, 51)",
          fontFamily: "Nunito, sans-serif",
          fontSize: "24px",
          fontWeight: 700,
          margin: 0,
          paddingRight: 4,
          lineHeight: 1.2,
        }}
      >
        <Box sx={{ fontSize: 24, lineHeight: 1 }}>⚡</Box>
        {t("boost-modal.title")}
      </Box>

      {/* Intro */}
      <Typography sx={{ mt: 3, ...bodyTextSx }}>
        <Trans
          i18nKey="boost-modal.intro"
          components={{ strong: strongComponent }}
        />
      </Typography>

      {/* How it works */}
      <Box sx={{ mt: 2 }}>
        <Typography sx={blueTitleSx}>{t("boost-modal.howTitle")}</Typography>

        <Typography sx={{ ...bodyTextSx, mb: 1.2 }}>
          <Trans
            i18nKey="boost-modal.howDesc"
            components={{ strong: strongComponent }}
          />
        </Typography>

        {/* Important reminder */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          sx={{ mt: 1 }}
        >
          <Box sx={{ mt: "2px" }}>⚠️</Box>
          <Box>
            <Typography
              sx={{
                ...bodyTextSx,
                fontWeight: 700,
                color: "rgba(0,0,0,0.7)",
                mb: 0.3,
              }}
            >
              {t("boost-modal.reminderTitle")}
            </Typography>
            <Typography sx={bodyTextSx}>
              {t("boost-modal.reminderDesc")}
            </Typography>
          </Box>
        </Stack>

        {/* Concrete example */}
        <Typography sx={{ ...blueTitleSx, mt: 2 }}>
          {t("boost-modal.exampleTitle")}
        </Typography>

        <Box
          sx={{
            backgroundColor: "rgba(0,0,0,0.04)",
            borderRadius: "10px",
            p: 1.6,
          }}
        >
          <Typography sx={{ ...bodyTextSx, mb: 1 }}>
            <Trans
              i18nKey="boost-modal.exampleIntro"
              components={{ strong: strongComponent }}
            />
          </Typography>

          <Typography sx={sectionLabelSx}>
            {t("boost-modal.withoutBoostTitle")}
          </Typography>

          <Stack spacing={0.6} sx={{ mb: 1.2 }}>
            {bulletRow("boost-modal.withoutBoost1")}
            {bulletRow("boost-modal.withoutBoost2")}
          </Stack>

          <Typography sx={sectionLabelSx}>
            {t("boost-modal.withBoostTitle")}
          </Typography>

          <Stack spacing={0.6}>
            {bulletRow("boost-modal.withBoost1")}
            {bulletRow("boost-modal.withBoost2")}
            {bulletRow("boost-modal.withBoost3")}
          </Stack>
        </Box>

        {/* Price */}
        <Typography sx={{ ...blueTitleSx, mt: 2 }}>
          {t("boost-modal.priceTitle")}
        </Typography>

        <Typography sx={bodyTextSx}>
          <Trans
            i18nKey="boost-modal.priceDesc"
            components={{ strong: strongComponent }}
          />
        </Typography>

        {/* CTA */}
        <Button
          disableElevation
          onClick={onClose}
          sx={{
            mt: 2.2,
            height: 34,
            borderRadius: "8px",
            px: 3,
            backgroundColor: "rgb(29, 106, 222)",
            color: "#fff",
            fontFamily: "Nunito, sans-serif",
            fontSize: "13px",
            fontWeight: 700,
            textTransform: "none",
            "&:hover": { backgroundColor: "rgb(30, 64, 175)" },
          }}
        >
          {t("boost-modal.gotIt")}
        </Button>
      </Box>
    </Dialog>
  );
};

export default BoostInfoModal;
